import React, { useContext } from "react";

import {
  Chip,
  ChipTypeMap,
  Grid,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import clsx from "clsx";
import { Clipboard } from "react-feather";

import mainStyles from "../../../styles";
import { SearchTableBodyRowMediaSearchProps } from "../../../types/search-display";
import { SnackbarContext } from "context/snackbar-context";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";

const TableBodyRowMediaSearch = ({
  row,
  mediaId,
}: SearchTableBodyRowMediaSearchProps) => {
  const classes = mainStyles();
  const theme = useTheme();

  const { copyToClipboard } = useContext(SnackbarContext);

  if (mediaId === "") {
    return null;
  }
  const userId = `${row.userId}` ? `${row.userId}` : "No user ID found";
  const orgId = `${row.organisationId}`
    ? `${row.organisationId}`
    : "No org ID found";
  const mediaType = row.scanInfo ? row.scanInfo.mediaType : null;
  const scanResult = row.scanInfo ? row.scanInfo.result : 0;
  const createdAt = row.createdAt ? row.createdAt : null;
  const bucket = row.prefixInfo ? row.prefixInfo.bucket : "No bucket found";
  const documentId = row.documentId ? row.documentId : "No document ID found";
  const scanResultMapping: {
    [key: number]: { string: string; color: ChipTypeMap["props"]["color"] };
  } = {
    0: { string: "Unspecified Result", color: "warning" },
    1: { string: "Safe", color: "success" },
    2: { string: "Unsafe", color: "error" },
    3: { string: "File Not Found", color: "error" },
    4: { string: "Scan Incomplete", color: "error" },
  };

  const getMediaTypeChip = () => {
    return (
      <>
        {row.scanInfo ? (
          <Chip
            size="small"
            color="info"
            className={clsx(classes.spaceLeft, classes.spaceRightXSmall)}
            label={mediaType}
            data-testid="media-type-chip"
          />
        ) : null}
      </>
    );
  };

  const getScanResultChip = () => {
    return (
      <Chip
        size="small"
        color={row.scanInfo ? scanResultMapping[scanResult].color : "error"}
        className={clsx(classes.spaceLeft, classes.spaceRightXSmall)}
        label={
          row.scanInfo ? scanResultMapping[scanResult].string : "No Scan Info"
        }
        data-testid="scan-result-chip"
      />
    );
  };

  return (
    <TableRow>
      <TableCell>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              <b>Media ID: </b>
              {mediaId}
              {getMediaTypeChip()}
              {getScanResultChip()}
            </Typography>

            <div onClick={() => copyToClipboard(row.userId)}>
              <Typography sx={classes.clickable} variant="body1">
                <b>Uploaded by: </b>
                {userId}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>

            <div onClick={() => copyToClipboard(orgId)}>
              <Typography
                sx={classes.clickable}
                variant="body1"
                paddingTop="5px"
              >
                <b>Organisation ID: </b>
                {orgId}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>

            <Typography variant="body1" paddingTop="5px">
              <b>Uploaded at: </b>
              {createdAt &&
                formatDateTimeDisplay(convertTimestampToDate(createdAt))}{" "}
            </Typography>

            <div onClick={() => copyToClipboard(documentId)}>
              <Typography
                sx={classes.clickable}
                variant="body1"
                paddingTop="5px"
              >
                <b>From Document: </b>
                {documentId}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>
            <Typography variant="body1" paddingTop="5px">
              <b>Bucket: </b>
              {bucket}{" "}
            </Typography>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default TableBodyRowMediaSearch;
